import React from 'react';
import { Tag } from '@/components/ui/tag';
import { Tooltip } from '@/components/ui/tooltip';
import {
  NoSymbolIcon,
  ExclamationCircleIcon,
  BugAntIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export type SpamCategory =
  | 'zero_value_evm'
  | 'failed_solana'
  | 'dust_spam'
  | 'phishing'
  | 'failed_transaction';

interface SpamBadgeProps {
  category: SpamCategory;
  note?: string;
  className?: string;
}

const SPAM_CONFIG: Record<SpamCategory, {
  label: string;
  icon: React.ReactNode;
  color: 'red' | 'orange' | 'yellow';
  description: string;
}> = {
  zero_value_evm: {
    label: 'Phishing Spam',
    icon: <NoSymbolIcon className="h-3.5 w-3.5" />,
    color: 'red',
    description: 'Zero-value transfer spam attack. No economic effect - only gas fee paid.'
  },
  failed_solana: {
    label: 'Failed Transaction',
    icon: <ExclamationCircleIcon className="h-3.5 w-3.5" />,
    color: 'orange',
    description: 'Solana transaction failed and was reverted. No tokens transferred.'
  },
  failed_transaction: {
    label: 'Failed',
    icon: <ExclamationCircleIcon className="h-3.5 w-3.5" />,
    color: 'orange',
    description: 'Transaction failed and was reverted.'
  },
  dust_spam: {
    label: 'Dust Spam',
    icon: <BugAntIcon className="h-3.5 w-3.5" />,
    color: 'orange',
    description: 'Unsolicited dust transfer (< $0.01 value).'
  },
  phishing: {
    label: 'Phishing Contract',
    icon: <ExclamationTriangleIcon className="h-3.5 w-3.5" />,
    color: 'red',
    description: 'Interaction with known phishing contract. Exercise caution.'
  }
};

export const SpamBadge: React.FC<SpamBadgeProps> = ({ category, note, className }) => {
  if (!category) return null;

  const config = SPAM_CONFIG[category];
  if (!config) return null;

  const tooltipText = `${config.label}: ${config.description}${note ? ` Details: ${note}` : ''} This transaction is excluded from tax calculations.`;

  return (
    <Tooltip title={tooltipText} placement="top">
      <Tag
        color={config.color}
        className={className}
      >
        {config.icon}
        {config.label}
      </Tag>
    </Tooltip>
  );
};

export default SpamBadge;
